import { call, put, take, takeLatest } from 'redux-saga/effects'
import { AmbassadorActions, AmbassadorTypes } from './ambassadorSlice'
import api from '../../services/api'
import { createUploadChannel } from '../listings/listingsSaga'

function* ambassadorApplicationStatusSaga() {
  const response = yield call(api.getAmbassadorApplicationStatus)
  if (response.ok) {
    yield put(AmbassadorActions.ambassadorApplicationStatusSuccess(response.data))
  } else {
    yield put(AmbassadorActions.ambassadorApplicationStatusFailure(response.problem))
  }
}

function* fetchLeadsSaga(action) {
  const response = yield call(api.getAmbassadorLeads, action.payload)
  if (response.ok) {
    yield put(AmbassadorActions.fetchLeadsSuccess(response.data))
  } else {
    yield put(AmbassadorActions.fetchLeadsFailure(response.problem))
  }
}

function* fetchAllVerifiedLeadsSaga(action) {
  const response = yield call(api.getAllVerifiedLeads, action.payload)
  if (response.ok) {
    yield put(AmbassadorActions.fetchAllVerifiedLeadsSuccess(response.data))
  } else {
    yield put(AmbassadorActions.fetchAllVerifiedLeadsFailure(response.problem))
  }
}

function* searchLeadsSaga(action) {
  const { query, filters, page, size } = action
  const response = yield call(api.searchLeads, query, filters, page, size)
  if (response.ok) {
    yield put(AmbassadorActions.searchingLeadsSuccess(response.data))
  } else {
    yield put(AmbassadorActions.searchingLeadsFailure(response.problem))
  }
}

function* fetchLeadSaga(action) {
  const response = yield call(api.getLead, action.id)
  if (response.ok) {
    yield put(AmbassadorActions.fetchLeadSuccess(response.data))
  } else {
    yield put(AmbassadorActions.fetchLeadFailure(response.problem))
  }
}

function* createLeadSaga(action) {
  const response = yield call(api.createAmbassadorLead, action.payload)
  if (response.ok) {
    yield put(AmbassadorActions.createLeadSuccess(response.data))
  } else {
    yield put(AmbassadorActions.createLeadFailure(response.problem))
  }
}

function* attachLeadVideoSaga(action) {
  const { leadId, file, title } = action

  const presignResponse = yield call(api.presignVideo)
  if (!presignResponse.ok) {
    yield put(AmbassadorActions.attachLeadVideoFailure(presignResponse.problem || 'Failed to get upload URL'))
    return
  }

  const { uploadUrl, s3Key } = presignResponse.data

  const channel = yield call(createUploadChannel, uploadUrl, file)

  try {
    while (true) {
      const event = yield take(channel)
      if (event.type === 'progress') {
        yield put(AmbassadorActions.attachLeadVideoProgress(event.percent))
      } else if (event.type === 'success') {
        break
      } else if (event.type === 'error') {
        yield put(AmbassadorActions.attachLeadVideoFailure(event.message))
        return
      }
    }
  } finally {
    if (channel.close) channel.close()
  }

  const response = yield call(api.uploadLeadVideo, leadId, s3Key, title)

  if (response.ok) {
    yield put(AmbassadorActions.attachLeadVideoSuccess(response.data))
  } else {
    yield put(AmbassadorActions.attachLeadVideoFailure(response.problem))
  }
}

function* leadUploadStatusSaga(action) {
  const response = yield call(api.uploadVideoStatus, action.jobId)
  if (response.ok) {
    yield put(AmbassadorActions.leadUploadStatusSuccess(response.data))
  } else {
    yield put(AmbassadorActions.leadUploadStatusFailure(response.problem))
  }
}

function* createLeadEnquirySaga(action) {
  const response = yield call(api.createLeadEnquiry, action.payload)
  if (response.ok) {
    yield put(AmbassadorActions.createLeadEnquirySuccess(response.data))
  } else {
    yield put(AmbassadorActions.createLeadEnquiryFailure(response.problem))
  }
}

function* fetchMyEnquiriesSaga(action) {
  const response = yield call(api.getMyLeadEnquiries, action.payload)
  if (response.ok) {
    yield put(AmbassadorActions.fetchMyEnquiriesSuccess(response.data))
  } else {
    yield put(AmbassadorActions.fetchMyEnquiriesFailure(response.problem))
  }
}

function* createLeadReviewSaga(action) {
  const response = yield call(api.createLeadReview, action.leadId, action.payload)
  if (response.ok) {
    yield put(AmbassadorActions.createLeadReviewSuccess(response.data))
  } else {
    yield put(AmbassadorActions.createLeadReviewFailure({
      problem: response.problem,
      status: response.status,
      message: response.data?.message,
    }))
  }
}

function* fetchLeadReviewsSaga(action) {
  const response = yield call(api.getLeadReviews, action.leadId, action.options)
  if (response.ok) {
    yield put(AmbassadorActions.fetchLeadReviewsSuccess(response.data))
  } else {
    yield put(AmbassadorActions.fetchLeadReviewsFailure(response.problem))
  }
}

export default function* ambassadorSaga() {
  yield takeLatest(AmbassadorTypes.AMBASSADOR_APPLICATION_STATUS_REQUEST, ambassadorApplicationStatusSaga)
  yield takeLatest(AmbassadorTypes.FETCH_LEADS_REQUEST, fetchLeadsSaga)
  yield takeLatest(AmbassadorTypes.FETCH_ALL_VERIFIED_LEADS_REQUEST, fetchAllVerifiedLeadsSaga)
  yield takeLatest(AmbassadorTypes.SEARCHING_LEADS_REQUEST, searchLeadsSaga)
  yield takeLatest(AmbassadorTypes.FETCH_LEAD_REQUEST, fetchLeadSaga)
  yield takeLatest(AmbassadorTypes.CREATE_LEAD_REQUEST, createLeadSaga)
  yield takeLatest(AmbassadorTypes.ATTACH_LEAD_VIDEO_REQUEST, attachLeadVideoSaga)
  yield takeLatest(AmbassadorTypes.LEAD_UPLOAD_STATUS_REQUEST, leadUploadStatusSaga)
  yield takeLatest(AmbassadorTypes.CREATE_LEAD_ENQUIRY_REQUEST, createLeadEnquirySaga)
  yield takeLatest(AmbassadorTypes.FETCH_MY_ENQUIRIES_REQUEST, fetchMyEnquiriesSaga)
  yield takeLatest(AmbassadorTypes.CREATE_LEAD_REVIEW_REQUEST, createLeadReviewSaga)
  yield takeLatest(AmbassadorTypes.FETCH_LEAD_REVIEWS_REQUEST, fetchLeadReviewsSaga)
}